import React, { useEffect, useState } from "react";
import { ShieldCheck, RefreshCw } from "lucide-react";
import api from "../lib/api";

const REASONS = {
  stripe_secret_key_missing_or_invalid: "Stripe secret key is missing or not a valid sk_/rk_ key",
  stripe_secret_key_is_placeholder: "Stripe secret key is a placeholder",
  stripe_mode_does_not_match_key: "STRIPE_MODE does not match the key's mode",
  stripe_webhook_secret_missing_or_invalid: "Webhook signing secret is missing or invalid",
};
const fmt = (t) => (t ? new Date(t).toLocaleString() : "Never");

function Row({ label, value, tone, testid }) {
  const color = tone === "bad" ? "text-[#E58CA0]" : tone === "good" ? "text-[#9FD8B0]" : "text-[#D8D2C9]";
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-[#D4AF37]/10 text-sm">
      <span className="text-[#9E978E]">{label}</span>
      <span className={color} data-testid={testid}>{value}</span>
    </div>
  );
}

export const AdminPaymentHealth = () => {
  const [h, setH] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [checkErr, setCheckErr] = useState("");
  const load = () => api.get("/admin/payments/health").then((r) => { setH(r.data); setErr(""); })
    .catch((e) => setErr(e.response?.status === 403 ? "Not authorized" : "Could not load payment health"));
  useEffect(() => { load(); }, []);
  const check = async () => {
    setBusy(true); setCheckErr("");
    try { await api.post("/admin/payments/health/check"); } catch (e) { setCheckErr(e.response?.data?.detail || "Check failed"); }
    setBusy(false); load();
  };
  if (err) return <div className="rounded-2xl border border-[#8B263E]/40 p-4 text-sm text-[#E58CA0] mb-6" data-testid="payment-health-error">{err}</div>;
  if (!h) return <div className="text-sm text-[#9E978E] mb-6" data-testid="payment-health-loading">Loading payment health…</div>;
  const c = h.last_connectivity_check;
  return (
    <div className="rounded-2xl border border-[#D4AF37]/20 bg-[#0C0A0A]/60 p-5 mb-6 max-w-2xl" data-testid="payment-health-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-serif text-lg text-[#FBF8F3] flex items-center gap-2"><ShieldCheck size={18} /> Payment Health</h3>
        <button onClick={check} disabled={busy || !!h.problem} data-testid="payment-health-check-btn"
          className="text-xs rounded-full border border-[#D4AF37]/40 px-3 py-1.5 text-[#D4AF37] disabled:opacity-40 flex items-center gap-1">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Check now
        </button>
      </div>
      <p className="text-xs text-[#E3C77A] mb-3" data-testid="payment-health-label">{h.label}</p>
      <Row label="Payments configured" testid="payment-health-configured" tone={h.configured ? "good" : "bad"}
        value={h.configured ? "Configured" : `Not configured — ${REASONS[h.problem] || h.problem}`} />
      <Row label="Detected key mode" testid="payment-health-key-mode" value={h.key_mode ? h.key_mode.toUpperCase() : "None"} tone={h.key_mode ? null : "bad"} />
      <Row label="STRIPE_MODE agrees" testid="payment-health-mode-match"
        value={h.stripe_mode_matches_key === null ? "Not set (key prefix decides)" : h.stripe_mode_matches_key ? "Yes" : "No — payments blocked"}
        tone={h.stripe_mode_matches_key === false ? "bad" : null} />
      <Row label="Webhook signing secret" testid="payment-health-webhook-secret" value={h.webhook_secret_present ? "Present" : "Missing"} tone={h.webhook_secret_present ? "good" : "bad"} />
      <Row label="Stripe connectivity (last check)" testid="payment-health-connectivity" tone={c ? (c.ok ? "good" : "bad") : null}
        value={c ? `${c.ok ? `OK · ${c.account_id}` : `Failed · ${c.error}`} · ${fmt(c.checked_at)}` : "Not checked yet"} />
      {checkErr && <p className="text-xs text-[#E58CA0] py-1" data-testid="payment-health-check-error">{checkErr}</p>}
      <Row label="Last verified signed webhook" testid="payment-health-last-webhook" value={h.last_verified_webhook ? `${fmt(h.last_verified_webhook.at)} · ${h.last_verified_webhook.type}` : "Never"} />
      <Row label="Last verified paid checkout" testid="payment-health-last-checkout" value={h.last_verified_paid_checkout ? `${fmt(h.last_verified_paid_checkout.at)} · via ${h.last_verified_paid_checkout.via}` : "Never"} />
      <Row label="End-to-end checkout verified" testid="payment-health-e2e" tone={h.end_to_end_verified ? "good" : null}
        value={h.end_to_end_verified ? "Yes — a real paid checkout was confirmed by a signed webhook" : "Not yet — no real checkout confirmed by a signed webhook"} />
      <Row label="Payments held for review" testid="payment-health-held" value={String(h.held_for_review ?? 0)} tone={h.held_for_review ? "bad" : null} />
    </div>
  );
};

export default AdminPaymentHealth;
